import { get } from 'lodash'

import { Colors, Services, getServicePathToAPI, projectName } from '@sigma-frontend-libs/commons'
import Macro from '@sigma-legacy-libs/cache'
import { DICTIONARIES, DICTIONARIES_STATUSES } from '@sigma-legacy-libs/essentials/lib/constants'

import { globalErrorHandler, proxy, request } from '@/utils'

import { rowStates } from '@/components/services/operatorGroups/utils'

import render from './render'
import { fullCountryMNC, fullCountryValue, isFullCountryValue } from './utils'

const cacheLifetime = 5 * 60 * 1000
const cacheCheckInterval = 60 * 1000
const operatorGroupsPageLimit = 300
const localCacheOptions = {
  ttl: cacheLifetime,
  ttlInterval: cacheCheckInterval,
  renewOnGet: false
}
const cache = new Macro(localCacheOptions)
const dictionariesPathToAPI = getServicePathToAPI(Services.dictionaries)
const pathToAPI = getServicePathToAPI(Services.operatorGroups)

const cachedFind = cache.wrapWithCache(async (key, query) => {
  return await request.find(pathToAPI, undefined, query)
})

export default {
  name: 'MCCMNC',

  mixins: [ proxy({ type: 'object' }) ],

  data() {
    return {
      tab: 'all',

      searchValue: undefined,
      dictionaryId: undefined,

      countries: [],
      groups: {},
      operators: {},

      analytics: {
        countries: 0,
        groups: 0,
        operators: 0
      },
      operatorGroupsCounters: {
        groupsByCountry: {},
        operatorsByGroup: {}
      },

      state: {
        countries: {},
        groups: {}
      },

      loading: {
        dictionary: false,
        search: false,
        analytics: false
      }
    }
  },

  computed: {
    /**
     * Возвращает локализованную строку аналитики по текущему словарю.
     *
     * @returns {string} Текст со счетчиками стран, групп и операторов.
     */
    analyticsLabel() {
      return this.getTranslate(`${Services.operatorGroups}.analytics`, {
        countries: this.analytics.countries,
        groups: this.analytics.groups,
        operators: this.analytics.operators
      })
    },

    /**
     * Возвращает страны, которые есть в текущем выбранном значении.
     *
     * @returns {Array} Список выбранных стран.
     */
    checkedCountries() {
      const keys = new Set(Object.keys(this.proxy))

      return this.countries.filter(country => {
        return keys.has(country.mcc)
      })
    },

    /**
     * Возвращает страны для текущей вкладки дерева.
     *
     * @returns {Array} Список стран для отображения.
     */
    visibleCountries() {
      switch (this.tab) {
        case 'checked': return this.checkedCountries
        default: return this.countries
      }
    }
  },

  watch: {
    searchValue(value) {
      this.search(value)
    },

    async tab(value) {
      if (value !== 'checked') {
        return
      }

      await this.loadCheckedCountriesGroups()
    }
  },

  async created() {
    await this.getDictionaryId()
    await Promise.all([
      this.search(this.searchValue),
      this.loadAnalytics()
    ])
  },

  methods: {
    /**
     * Преобразует входное значение перед записью в связанное значение.
     *
     * @param {Object|undefined} data Входное значение.
     * @returns {Object} Подготовленное значение.
     */
    _inputFilter(data) {
      if (!data) {
        return {}
      }

      return data
    },

    /**
     * Прокручивает список так, чтобы нужная строка была ближе к центру.
     *
     * @param {string} reference Имя ссылки на строку дерева.
     * @returns {void}
     */
    scrollTree(reference) {
      this.$nextTick(() => {
        let referenceElement = this.$refs[reference]
        if (Array.isArray(referenceElement)) {
          referenceElement = referenceElement[0]
        }
        if (!referenceElement || !referenceElement.isConnected) {
          return
        }

        const scrollableElement = this.$refs.scrollableElement
        if (!scrollableElement) {
          return
        }

        const referenceRectangle = referenceElement.getBoundingClientRect()
        const scrollableRectangle = scrollableElement.getBoundingClientRect()
        const referenceOffsetTop = referenceRectangle.top - scrollableRectangle.top + scrollableElement.scrollTop
        const targetScrollTop = referenceOffsetTop - (scrollableElement.clientHeight - referenceRectangle.height) / 2
        const maxScrollTop = Math.max(scrollableElement.scrollHeight - scrollableElement.clientHeight, 0)
        const boundedScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScrollTop)

        scrollableElement.scrollTo({
          top: boundedScrollTop,
          behavior: 'smooth'
        })
      })
    },

    /**
     * Возвращает цвет кнопки вкладки с учетом текущей вкладки.
     *
     * @param {string} tab Имя вкладки.
     * @returns {string} Цвет кнопки.
     */
    getTabColor(tab) {
      if (this.tab === tab) {
        return Colors.primary
      }

      return Colors.grey
    },
    /**
     * Возвращает внутренний ключ строки страны.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {string} Внутренний ключ строки страны.
     */
    getCountryKey(countryData) {
      const { country, mcc } = countryData

      return `${country}:${mcc}`
    },
    /**
     * Возвращает внутренний ключ строки группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.country Название страны.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @returns {string} Внутренний ключ строки группы.
     */
    getGroupKey(groupData) {
      const { country, group, mcc } = groupData

      return `${country}:${mcc}:${group}`
    },
    /**
     * Возвращает состояние строки страны.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {string} Состояние строки страны.
     */
    getCountryState(countryData) {
      const { mcc } = countryData
      if (this.tab === 'checked' && this.isCountryPartial(mcc)) {
        return rowStates.opened
      }

      return this.state.countries[this.getCountryKey(countryData)] || rowStates.closed
    },
    /**
     * Возвращает состояние строки группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.country Название страны.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @returns {string} Состояние строки группы.
     */
    getGroupState(groupData) {
      if (this.tab === 'checked') {
        if (this.isGroupChecked(groupData)) {
          return rowStates.closed
        }

        return rowStates.opened
      }

      return this.state.groups[this.getGroupKey(groupData)] || rowStates.closed
    },
    /**
     * Возвращает количество групп страны по полному словарю.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {number} Количество групп страны.
     */
    getCountryGroupsCount(countryData) {
      return this.operatorGroupsCounters.groupsByCountry[this.getCountryKey(countryData)] || 0
    },
    /**
     * Возвращает количество операторов группы по полному словарю.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.country Название страны.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @returns {number} Количество операторов группы.
     */
    getGroupOperatorsCount(groupData) {
      return this.operatorGroupsCounters.operatorsByGroup[this.getGroupKey(groupData)] || 0
    },
    /**
     * Возвращает подпись строки страны.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {string} Подпись строки страны.
     */
    getCountryHint(countryData) {
      const groups = this.getCountryGroupsCount(countryData)
      if (groups > 0) {
        return this.getTranslate(`${Services.operatorGroups}.hints.tree.groupsCount`, { groups })
      }

      return this.getTranslate(`${Services.operatorGroups}.hints.tree.country`)
    },
    /**
     * Возвращает подпись строки группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.country Название страны.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @returns {string} Подпись строки группы.
     */
    getGroupHint(groupData) {
      const operators = this.getGroupOperatorsCount(groupData)
      if (operators > 0) {
        return this.getTranslate(`${Services.operatorGroups}.hints.tree.operatorsCount`, { operators })
      }

      return this.getTranslate(`${Services.operatorGroups}.hints.tree.group`)
    },

    /**
     * Проверяет, выбрана ли страна полностью.
     *
     * @param {string} mcc Код страны.
     * @returns {boolean} Признак полного выбора страны.
     */
    isCountryChecked(mcc) {
      const checked = get(this.proxy, mcc, false)

      return isFullCountryValue(checked)
    },
    /**
     * Проверяет, выбрана ли часть операторов страны.
     *
     * @param {string} mcc Код страны.
     * @returns {boolean} Признак частичного выбора страны.
     */
    isCountryPartial(mcc) {
      const checked = get(this.proxy, mcc, false)

      return Array.isArray(checked) && checked.length > 0
    },
    /**
     * Проверяет, выбрана ли группа полностью.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.mcc Код страны.
     * @param {Array} groupData.operators Операторы группы.
     * @returns {boolean} Признак полного выбора группы.
     */
    isGroupChecked(groupData) {
      const { mcc, operators } = groupData
      if (this.isCountryChecked(mcc)) {
        return true
      }

      const checked = this.proxy[mcc]
      if (!Array.isArray(checked)) {
        return false
      }

      let groupOperators = []
      if (Array.isArray(operators)) {
        groupOperators = operators
      }

      if (!groupOperators.length) {
        return false
      }

      const allOperatorsBelongToCountry = groupOperators.every(operator => {
        return operator.mcc === mcc
      })

      if (!allOperatorsBelongToCountry) {
        return false
      }

      return groupOperators.every(operator => {
        return checked.includes(operator.mnc)
      })
    },
    /**
     * Проверяет, выбрана ли часть операторов группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.mcc Код страны.
     * @param {Array} groupData.operators Операторы группы.
     * @returns {boolean} Признак частичного выбора группы.
     */
    isGroupPartial(groupData) {
      const { mcc, operators } = groupData
      if (this.isGroupChecked(groupData)) {
        return false
      }

      const checked = this.proxy[mcc]
      if (!Array.isArray(checked)) {
        return false
      }

      let groupOperators = []
      if (Array.isArray(operators)) {
        groupOperators = operators
      }

      const allOperatorsBelongToCountry = groupOperators.every(operator => {
        return operator.mcc === mcc
      })

      if (!allOperatorsBelongToCountry) {
        return false
      }

      return groupOperators.some(operator => {
        return checked.includes(operator.mnc)
      })
    },
    /**
     * Проверяет, выбран ли оператор страны.
     *
     * @param {string} mcc Код страны.
     * @param {string} mnc Код оператора.
     * @returns {boolean} Признак выбора оператора.
     */
    isOperatorChecked(mcc, mnc) {
      const checked = this.proxy[mcc]
      if (Array.isArray(checked)) {
        return checked.includes(mnc)
      }

      return false
    },

    /**
     * Возвращает группы страны в формате, удобном для дерева.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {Array} Список групп страны.
     */
    getGroupsByMCC(countryData) {
      const { country, mcc } = countryData
      const groups = this.groups[country] || {}
      const result = []

      for (const group in groups) {
        const operators = groups[group].filter(operator => {
          return operator.mcc === mcc
        })
        if (!operators.length) {
          continue
        }

        result.push({
          country,
          group,
          mcc,
          operators
        })
      }

      return result
    },
    /**
     * Возвращает выбранных операторов группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.mcc Код страны.
     * @param {Array} groupData.operators Операторы группы.
     * @returns {Array} Список выбранных операторов.
     */
    getCheckedOperators(groupData) {
      const { mcc, operators } = groupData
      let result = []
      if (Array.isArray(operators)) {
        result = operators
      }

      if (this.isCountryChecked(mcc)) {
        return result
      }

      const checked = this.proxy[mcc]
      if (!Array.isArray(checked)) {
        return []
      }

      return result.filter(operator => {
        return checked.includes(operator.mnc)
      })
    },
    /**
     * Возвращает группы, в которых есть выбранные операторы.
     *
     * @param {Array} groups Группы страны.
     * @returns {Array} Группы с выбранными операторами.
     */
    getCheckedGroups(groups) {
      const result = []
      for (const group of groups) {
        const operators = this.getCheckedOperators(group)
        if (!operators.length) {
          continue
        }

        result.push(group)
      }

      return result
    },
    /**
     * Возвращает группы страны, видимые на текущей вкладке.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {Array} Список видимых групп.
     */
    getVisibleGroups(countryData) {
      const { mcc } = countryData
      if (this.tab === 'checked' && this.isCountryChecked(mcc)) {
        return []
      }

      const groups = this.getGroupsByMCC(countryData)
      if (this.tab === 'checked') {
        return this.getCheckedGroups(groups)
      }

      if (this.getCountryState(countryData) !== rowStates.opened) {
        return []
      }

      return groups
    },
    /**
     * Возвращает операторов группы, видимых на текущей вкладке.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @param {Array} groupData.operators Операторы группы.
     * @returns {Array} Список видимых операторов.
     */
    getVisibleOperators(groupData) {
      const { operators } = groupData
      let result = []
      if (Array.isArray(operators)) {
        result = operators
      }

      if (this.tab === 'checked') {
        if (this.isGroupChecked(groupData)) {
          return []
        }

        return this.getCheckedOperators(groupData)
      }

      if (this.getGroupState(groupData) !== rowStates.opened) {
        return []
      }

      return result
    },

    /**
     * Собирает аналитику по строкам словаря групп операторов.
     *
     * @param {Array} operatorGroups Строки словаря групп операторов.
     * @returns {Object} Счетчики стран, групп и операторов.
     */
    aggregateOperatorGroups(operatorGroups) {
      const countries = new Set()
      const groups = new Set()
      const operators = new Set()

      for (const operatorGroup of operatorGroups) {
        if (operatorGroup.mcc) {
          countries.add(operatorGroup.mcc)
        }
        if (operatorGroup.group) {
          groups.add(operatorGroup.group)
        }
        if (operatorGroup.operator) {
          operators.add(operatorGroup.operator)
        }
      }

      return {
        countries: countries.size,
        groups: groups.size,
        operators: operators.size
      }
    },
    /**
     * Собирает счетчики строк дерева по полному словарю.
     *
     * @param {Array} operatorGroups Строки словаря групп операторов.
     * @returns {Object} Счетчики строк дерева.
     */
    aggregateOperatorGroupsCounters(operatorGroups) {
      const groupsByCountrySets = {}
      const groupsByCountry = {}
      const operatorsByGroup = {}

      for (const operatorGroup of operatorGroups) {
        const hasGroupData = operatorGroup.country && operatorGroup.group && operatorGroup.mcc
        if (!hasGroupData) {
          continue
        }

        const countryKey = this.getCountryKey(operatorGroup)
        if (!groupsByCountrySets[countryKey]) {
          groupsByCountrySets[countryKey] = new Set()
        }
        groupsByCountrySets[countryKey].add(operatorGroup.group)

        if (operatorGroup.operator) {
          const groupKey = this.getGroupKey(operatorGroup)
          if (!operatorsByGroup[groupKey]) {
            operatorsByGroup[groupKey] = 0
          }

          operatorsByGroup[groupKey] += 1
        }
      }

      for (const countryKey in groupsByCountrySets) {
        groupsByCountry[countryKey] = groupsByCountrySets[countryKey].size
      }

      return {
        groupsByCountry,
        operatorsByGroup
      }
    },

    /**
     * Загружает идентификатор словаря групп операторов.
     *
     * @returns {Promise<void>}
     */
    async getDictionaryId() {
      try {
        this.loading.dictionary = true
        if (!this.dictionaryId) {
          const query = {
            type: DICTIONARIES['phone:opgroups'],
            status: DICTIONARIES_STATUSES.activated,
            $limit: 1
          }
          const response = await request.find(dictionariesPathToAPI, undefined, query)
          const dictionary = response?.data?.[0]
          if (dictionary) {
            this.dictionaryId = dictionary.id
          }
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading.dictionary = false
      }
    },
    /**
     * Загружает строки групп и операторов для страны.
     *
     * @param {string} country Название страны.
     * @returns {Promise<Array>} Строки групп и операторов.
     */
    async getGroups(country) {
      try {
        const query = {
          DictionaryId: this.dictionaryId,
          country
        }
        const key = [ projectName, Services.operatorGroups, 'find', JSON.stringify(query) ].filter(Boolean).join(':')
        const response = await cachedFind(key, query)
        if (response) {
          return response.data || []
        }
      } catch (error) {
        globalErrorHandler(error)
      }

      return []
    },
    /**
     * Загружает одну страницу строк словаря групп операторов.
     *
     * @param {number} offset Смещение страницы строк словаря.
     * @returns {Promise<Object>} Страница строк словаря.
     */
    async getOperatorGroupsPage(offset) {
      const query = {
        DictionaryId: this.dictionaryId,
        $limit: operatorGroupsPageLimit,
        $offset: offset
      }
      const key = [ projectName, Services.operatorGroups, 'find', JSON.stringify(query) ].filter(Boolean).join(':')
      const response = await cachedFind(key, query)

      return response || { data: [] }
    },
    /**
     * Загружает все строки словаря групп операторов постранично.
     *
     * @returns {Promise<Array>} Все строки текущего словаря.
     */
    async getAllOperatorGroups() {
      const result = []
      let offset = 0
      let total = operatorGroupsPageLimit

      while (offset < total) {
        const response = await this.getOperatorGroupsPage(offset)
        let data = []
        if (Array.isArray(response.data)) {
          data = response.data
        }

        result.push(...data)

        if (Number.isFinite(response.total)) {
          total = response.total
        } else {
          total = result.length
        }

        if (!data.length) {
          break
        }

        offset += operatorGroupsPageLimit
      }

      return result
    },
    /**
     * Группирует строки операторов по названию группы.
     *
     * @param {Array} groups Строки групп и операторов.
     * @returns {Object} Операторы, сгруппированные по группе.
     */
    getCountryGroups(groups) {
      const result = {}
      for (const group of groups) {
        if (!result[group.group]) {
          result[group.group] = []
        }

        result[group.group].push(group)
        result[group.group].sort((firstOperator, secondOperator) => {
          const mncOrder = firstOperator.mnc - secondOperator.mnc
          const operatorOrder = firstOperator.operator - secondOperator.operator

          return mncOrder || operatorOrder
        })
      }

      return result
    },
    /**
     * Загружает аналитику текущего словаря групп операторов.
     *
     * @returns {Promise<void>}
     */
    async loadAnalytics() {
      if (!this.dictionaryId) {
        return
      }

      try {
        this.loading.analytics = true
        const operatorGroups = await this.getAllOperatorGroups()
        this.analytics = this.aggregateOperatorGroups(operatorGroups)
        this.operatorGroupsCounters = this.aggregateOperatorGroupsCounters(operatorGroups)
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading.analytics = false
      }
    },
    /**
     * Заполняет выбранные операторы групп по выбранным кодам страны.
     *
     * @param {string} mcc Код страны.
     * @param {Array} groups Группы страны.
     * @returns {void}
     */
    fillOperators(mcc, groups) {
      const checked = this.proxy[mcc]
      if (!Array.isArray(checked) || !checked.length) {
        return
      }

      for (const mnc of checked) {
        for (const groupData of groups) {
          const { group } = groupData
          if (!Array.isArray(this.operators[group])) {
            this.$set(this.operators, group, [])
          }

          if (!this.operators[group].includes(mnc)) {
            this.operators[group].push(mnc)
          }
        }
      }
    },
    /**
     * Загружает группы и операторов страны, если они еще не загружены.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {Promise<void>}
     */
    async loadCountryGroups({ country, mcc }) {
      if (this.groups[country]) {
        this.fillOperators(mcc, this.getGroupsByMCC({
          country,
          mcc
        }))

        return
      }

      const groupsInCountry = await this.getGroups(country)
      const groups = this.getCountryGroups(groupsInCountry)
      this.$set(this.groups, country, groups)
      this.fillOperators(mcc, this.getGroupsByMCC({
        country,
        mcc
      }))
    },
    /**
     * Загружает группы стран с частичным выбором для вкладки выбранных.
     *
     * @returns {Promise<void>}
     */
    async loadCheckedCountriesGroups() {
      for (const country of this.checkedCountries) {
        if (this.isCountryChecked(country.mcc)) {
          continue
        }

        await this.loadCountryGroups(country)
      }
    },
    /**
     * Ищет страны по строке поиска и обновляет список стран.
     *
     * @param {string|undefined} searchValue Строка поиска.
     * @returns {Promise<void>}
     */
    async search(searchValue) {
      if (!this.dictionaryId) {
        return
      }

      try {
        this.loading.search = true
        const query = {
          DictionaryId: this.dictionaryId,
          $search: searchValue,
          $scope: [ 'Country' ],
          $limit: operatorGroupsPageLimit
        }
        const key = [ projectName, Services.operatorGroups, 'find', JSON.stringify(query) ].filter(Boolean).join(':')
        const response = await cachedFind(key, query)
        if (Array.isArray(response?.data)) {
          this.countries = response.data
        } else {
          this.countries = []
        }
        if (this.tab === 'checked') {
          await this.loadCheckedCountriesGroups()
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading.search = false
      }
    },

    /**
     * Переключает раскрытие страны и загружает ее группы при раскрытии.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {Promise<void>}
     */
    async toggleCountryState(countryData) {
      const { country, mcc } = countryData
      const key = this.getCountryKey(countryData)
      try {
        if (this.isCountryChecked(mcc)) {
          return
        }

        const state = this.getCountryState(countryData)
        switch (state) {
          case rowStates.closed: {
            this.$set(this.state.countries, key, rowStates.loading)
            await this.loadCountryGroups({
              country,
              mcc
            })
            for (const stateKey in this.state.countries) {
              this.$delete(this.state.countries, stateKey)
            }
            this.$set(this.state.countries, key, rowStates.opened)
            break
          }
          case rowStates.opened: {
            this.$set(this.state.countries, key, rowStates.closed)
            break
          }
          default: { break }
        }
      } catch (error) {
        globalErrorHandler(error)
        this.$delete(this.groups, country)
      }
    },
    /**
     * Переключает раскрытие группы, если группа не выбрана полностью.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @returns {void}
     */
    toggleGroupState(groupData) {
      if (this.isGroupChecked(groupData)) {
        return
      }

      const key = this.getGroupKey(groupData)
      const state = this.getGroupState(groupData)
      switch (state) {
        case rowStates.closed: {
          this.$set(this.state.groups, key, rowStates.opened)
          break
        }
        case rowStates.opened: {
          this.$set(this.state.groups, key, rowStates.closed)
          break
        }
        default: { break }
      }
    },
    /**
     * Переключает выбор всей страны.
     *
     * @param {Object} countryData Данные страны.
     * @param {string} countryData.country Название страны.
     * @param {string} countryData.mcc Код страны.
     * @returns {void}
     */
    toggleCountrySelection(countryData) {
      const { country, mcc } = countryData
      const checked = get(this.proxy, mcc, false)
      switch (true) {
        case Array.isArray(checked):
        case checked === false: {
          this.$set(this.proxy, mcc, fullCountryValue)
          this.$set(this.state.countries, this.getCountryKey(countryData), rowStates.closed)
          break
        }
        case this.isCountryChecked(mcc): {
          this.$delete(this.proxy, mcc)
          const groups = this.groups[country] || {}
          for (const group in groups) {
            this.$delete(this.operators, group)
          }
          break
        }
        default: { break }
      }
    },
    /**
     * Переключает выбор всех операторов группы.
     *
     * @param {Object} groupData Данные группы.
     * @param {string} groupData.group Название группы.
     * @param {string} groupData.mcc Код страны.
     * @param {Array} groupData.operators Операторы группы.
     * @returns {void}
     */
    toggleGroupSelection(groupData) {
      const { group, mcc, operators } = groupData
      const mncs = operators.map(({ mnc }) => {
        return mnc
      })
      if (Array.isArray(this.proxy[mcc])) {
        for (const mnc of mncs) {
          const index = this.proxy[mcc].indexOf(mnc)
          if (index >= 0) {
            this.proxy[mcc].splice(index, 1)
          } else {
            this.proxy[mcc].push(mnc)
          }
        }
        if (this.proxy[mcc].length === 0) {
          this.$delete(this.proxy, mcc)
        }
      } else {
        this.$set(this.proxy, mcc, [])
        this.proxy[mcc].push(...mncs)
      }

      if (Array.isArray(this.operators[group])) {
        for (const mnc of mncs) {
          const index = this.operators[group].indexOf(mnc)
          if (index >= 0) {
            this.operators[group].splice(index, 1)
          } else {
            this.operators[group].push(mnc)
          }
        }
        if (this.operators[group].length === 0) {
          this.$delete(this.operators, group)
        }
      } else {
        this.$set(this.operators, group, [])
        this.operators[group].push(...mncs)
      }
    },
    /**
     * Переключает выбор одного оператора.
     *
     * @param {Object} operatorData Данные оператора.
     * @param {string} operatorData.group Название группы.
     * @param {string} operatorData.mcc Код страны.
     * @param {string} operatorData.mnc Код оператора.
     * @returns {void}
     */
    toggleOperatorSelection(operatorData) {
      const { group, mcc, mnc } = operatorData
      if (!Array.isArray(this.proxy[mcc])) {
        this.$set(this.proxy, mcc, [])
      }

      if (Array.isArray(this.proxy[mcc])) {
        const index = this.proxy[mcc].indexOf(mnc)
        if (index >= 0) {
          this.proxy[mcc].splice(index, 1)
          if (this.proxy[mcc].length === 0) {
            this.$delete(this.proxy, mcc)
          }
        } else {
          this.proxy[mcc].push(mnc)
        }
      }

      if (!Array.isArray(this.operators[group])) {
        this.$set(this.operators, group, [])
      }

      if (Array.isArray(this.operators[group])) {
        const index = this.operators[group].indexOf(mnc)
        if (index >= 0) {
          this.operators[group].splice(index, 1)
        } else {
          this.operators[group].push(mnc)
        }

        if (this.operators[group].length === 0) {
          this.$delete(this.operators, group)
        }
      }
    },

    /**
     * Разбирает строки импорта и переносит их в выбранное значение.
     *
     * @param {Array} data Строки с кодами стран и операторов.
     * @returns {void}
     */
    parser(data) {
      for (const { mcc, mnc } of data) {
        if (!mcc) {
          continue
        }

        if (mnc) {
          const shouldResetMNCList = !this.proxy[mcc] ||
            typeof this.proxy[mcc] === 'string' && this.proxy[mcc] === fullCountryValue

          if (shouldResetMNCList) {
            this.$set(this.proxy, mcc, [])
            this.proxy[mcc].push(fullCountryMNC)
          }

          if (Array.isArray(this.proxy[mcc]) && !this.proxy[mcc].includes(mnc)) {
            this.proxy[mcc].push(mnc)
          }

          continue
        }

        if (Array.isArray(this.proxy[mcc])) {
          if (!this.proxy[mcc].includes(fullCountryMNC)) {
            this.proxy[mcc].unshift(fullCountryMNC)
          }

          continue
        }

        if (!this.proxy[mcc]) {
          this.$set(this.proxy, mcc, fullCountryValue)
        }
      }
    }
  },

  render
}
